import React, { lazy } from 'react';
import { isParseError } from 'lib/html2json';

import Grid from '@mui/material/Grid';
import FAQ from 'components/FAQ/FAQ';
import DynamicCollection from 'components/Collection/DynamicCollection';
import MastercardProductOptionGridHelper from 'components/Product/MastercardProductOptionGridHelper';
import DynamicBrandList from 'components/Brand/DynamicBrandList';
import ProductCarouselList from 'components/Carousel/ProductCarousel';
import Banner from 'components/Banner/Banner';
import debug from 'debug';
import Box from '@mui/system/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

import { Link as RouterLink } from 'react-router-dom';
import SendIcon from '@mui/icons-material/ArrowForward';
import { CGProduct } from 'types/shopify';
import CustomisationSection from 'components/Corporate/CustomisationSection';
import CardGiftSection from 'components/Home/CardGiftSection';
// import EnquiryForm from 'components/Form/EnquiryForm';
// import ContactForm from 'components/Form/ContactForm';
// import DynamicCarousel from 'components/DynamicCarousel/DynamicCarousel';
// import DynamicCarouselSlide from 'components/DynamicCarousel/DynamicCarouselSlide';
import set from 'lodash/set';

const VideoMessageSection = lazy(() => import('components/Corporate/VideoMessageSection' /* webpackChunkName: "videoMessageSection" */));
const DynamicCarousel = lazy(() => import('components/DynamicCarousel/DynamicCarousel' /* webpackChunkName: "dynamicCarousel" */));
const DynamicCarouselSlide = lazy(() => import('components/DynamicCarousel/DynamicCarouselSlide' /* webpackChunkName: "dynamicCarousel" */));
const EnquiryForm = lazy(() => import('components/Form/EnquiryForm' /* webpackChunkName: "enquiryForm" */));
const ContactForm = lazy(() => import('components/Form/ContactForm' /* webpackChunkName: "contactForm" */));

const d = debug('descriptionHelper');

interface DataProps {
  products?: CGProduct[];
  backgroundImage?: string;
  title?: string;
}
interface DescriptionHelperProps {
  html?: string;
  data?: DataProps;
}

const extractGenericAttributes = (docNode: any) => {
  const foundAttributes = {};

  const attrCount = docNode.attributes.length || 0;
  for (let x = 0; x < attrCount; x++) {
    const attr = docNode.attributes.item(x);
    if (attr && attr?.name) {
      let value = attr?.value;
      if (value === 'true') value = true;
      if (value === 'false') value = false;
      set(foundAttributes, attr?.name, value);
    }
  }
  return foundAttributes;
};

const convertNodeToReact = (node: Node, idx: number, data?: DataProps) => {
  if (node.nodeName === '#text') return node.textContent;
  let children: any = [];
  node.childNodes.forEach((node: Node, idx: number) => {
    children.push(convertNodeToReact(node, idx, data));
  });
  let nodeName: any = node.nodeName;
  let nodeProps: any = {
    key: idx,
  };
  switch (nodeName) {
    case 'dynamicCarousel':
      nodeName = DynamicCarousel;
      // Filter children to just objects (slides...)
      children = children.filter((child: any) => typeof child === 'object');
      // Remove useless breaks.
      children = children.filter((child: any) => child.type !== 'br');
      break;
    case 'dynamicCarouselSlide':
      nodeName = DynamicCarouselSlide;
      nodeProps.item = extractGenericAttributes(node);
      break;
    case 'form':
      nodeName = 'div';
      // @ts-ignore
      if (node.hasAttribute('name') && node.getAttribute('name') === 'EnquiryForm') {
        nodeName = EnquiryForm;
        children = null;
      }
      // @ts-ignore
      if (node.hasAttribute('name') && node.getAttribute('name') === 'ContactForm') {
        nodeName = ContactForm;
        children = null;
      }
      break;
    case 'a':
      nodeName = 'a';
      // @ts-ignore
      if (node.hasAttribute('href')) {
        // @ts-ignore
        nodeProps.href = node.getAttribute('href');
      }
      break;
    case 'divider':
      nodeName = Divider;
      children = null;
      break;
    case 'newline':
      nodeName = Box;
      // @ts-ignore
      if (node.hasAttribute('height')) {
        // @ts-ignore
        nodeProps.height = node.getAttribute('height');
      } else {
        // @ts-ignore
        nodeProps.height = ['10px', '20px'];
      }
      children = null;
      break;
    case 'br':
      nodeName = 'br';
      children = null;
      break;
    case 'configureButtonList':
      nodeName = MastercardProductOptionGridHelper;
      break;
    case 'slider':
      // @ts-ignore
      if (node.hasAttribute('handle')) {
        nodeName = DynamicCollection;
        // @ts-ignore
        nodeProps.handle = node.getAttribute('handle');
        // @ts-ignore
        if (node.hasAttribute('title')) {
          // @ts-ignore
          nodeProps.title = node.getAttribute('title');
        }
        // @ts-ignore
        if (node.hasAttribute('cardSize')) {
          // @ts-ignore
          nodeProps.cardSize = node.getAttribute('cardSize');
        }
        // @ts-ignore
        if (node.hasAttribute('padFirstAndLast')) {
          // @ts-ignore
          nodeProps.padFirstAndLast = node.getAttribute('padFirstAndLast') === 'true' ? true : false;
        }
      } else {
        return null;
      }
      break;
    case 'grid':
      // @ts-ignore
      if (node.hasAttribute('handle')) {
        nodeName = DynamicCollection;
        nodeProps.variant = 'GRID';
        // @ts-ignore
        nodeProps.handle = node.getAttribute('handle');
        // @ts-ignore
        if (node.hasAttribute('title')) {
          // @ts-ignore
          nodeProps.title = node.getAttribute('title');
        }
        // nodeProps.title =
      } else {
        return null;
      }
      break;
    case 'brands':
      // @ts-ignore
      if (node.hasAttribute('items')) {
        nodeName = DynamicBrandList;
        // @ts-ignore
        nodeProps.items = node.getAttribute('items');
        // @ts-ignore
        if (node.hasAttribute('title')) {
          // @ts-ignore
          nodeProps.title = node.getAttribute('title');
        }
        // @ts-ignore
        if (node.hasAttribute('buttonTitle')) {
          // @ts-ignore
          nodeProps.buttonTitle = node.getAttribute('buttonTitle');
        }
        children = null;
      } else {
        return null;
      }
      break;
    case 'faq':
      // @ts-ignore
      if (node.hasAttribute('tags')) {
        nodeName = FAQ;
        // @ts-ignore
        nodeProps.handle = node.getAttribute('tags');
        // @ts-ignore
        if (node.hasAttribute('title')) {
          // @ts-ignore
          nodeProps.title = node.getAttribute('title');
        }
        children = null;
      } else {
        return null;
      }
      break;
    case 'image':
      // @ts-ignore
      if (node.hasAttribute('src')) {
        nodeName = Box;
        nodeProps.component = 'img';
        // @ts-ignore
        nodeProps.src = node.getAttribute('src');
        // @ts-ignore
        if (node.hasAttribute('width')) {
          // @ts-ignore
          const width = node.getAttribute('width');
          if (width.includes(',')) {
            nodeProps.width = width.split(',');
          } else {
            nodeProps.width = width;
          }
        }
        children = null;
      } else {
        return null;
      }
      break;
    case 'productCarouselList':
      if (data) {
        nodeName = ProductCarouselList;
        nodeProps.products = data.products || [];
        nodeProps.backgroundImage = data.backgroundImage || [];
        nodeProps.title = data.title || [];
        nodeProps.buttonLabel = 'Enquire Now';
        nodeProps.source = 'home';
        // @ts-ignore
        if (node.hasAttribute('description')) {
          // @ts-ignore
          nodeProps.description = node.getAttribute('description');
        }
        // @ts-ignore
        if (node.hasAttribute('styleName')) {
          // @ts-ignore
          nodeProps.styleName = node.getAttribute('styleName');
        }
        // @ts-ignore
        if (node.hasAttribute('buttonLabel')) {
          // @ts-ignore
          nodeProps.buttonLabel = node.getAttribute('buttonLabel');
        }
        // @ts-ignore
        if (node.hasAttribute('mode')) {
          // @ts-ignore
          nodeProps.mode = node.getAttribute('mode');
        }
        children = null;
      } else {
        return null;
      }
      break;
    case 'container':
      nodeName = Container;
      break;
    case 'gridcontainer':
      nodeName = Grid;
      nodeProps.container = true;
      nodeProps.spacing = 3;
      break;
    case 'column1':
      nodeName = Grid;
      nodeProps.item = true;
      nodeProps.sm = 1;
      break;
    case 'column2':
      nodeName = Grid;
      nodeProps.item = true;
      nodeProps.sm = 2;
      break;
    case 'column3':
      nodeName = Grid;
      nodeProps.item = true;
      nodeProps.sm = 3;
      break;
    case 'column4':
      nodeName = Grid;
      nodeProps.item = true;
      nodeProps.sm = 4;
      break;
    case 'column5':
      nodeName = Grid;
      nodeProps.item = true;
      nodeProps.sm = 5;
      break;
    case 'column6':
      nodeName = Grid;
      nodeProps.item = true;
      nodeProps.sm = 6;
      break;
    case 'column7':
      nodeName = Grid;
      nodeProps.item = true;
      nodeProps.sm = 7;
      break;
    case 'column8':
      nodeName = Grid;
      nodeProps.item = true;
      nodeProps.sm = 8;
      break;
    case 'column9':
      nodeName = Grid;
      nodeProps.item = true;
      nodeProps.sm = 9;
      break;
    case 'column10':
      nodeName = Grid;
      nodeProps.item = true;
      nodeProps.sm = 10;
      break;
    case 'column11':
      nodeName = Grid;
      nodeProps.item = true;
      nodeProps.sm = 11;
      break;
    case 'column12':
      nodeName = Grid;
      nodeProps.item = true;
      nodeProps.sm = 12;
      break;
    case 'box':
      nodeName = Box;
      // @ts-ignore
      if (node.hasAttribute('component')) {
        // @ts-ignore
        nodeProps.component = node.getAttribute('component');
      }
      // @ts-ignore
      if (node.hasAttribute('display')) {
        // @ts-ignore
        nodeProps.display = node.getAttribute('display');
      }
      // @ts-ignore
      if (node.hasAttribute('padding')) {
        // @ts-ignore
        const padding = node.getAttribute('padding');
        if (padding.includes(',')) {
          nodeProps.padding = padding.split(',');
        } else {
          nodeProps.padding = padding;
        }
      }
      // @ts-ignore
      if (node.hasAttribute('alignItems')) {
        // @ts-ignore
        nodeProps.alignItems = node.getAttribute('alignItems');
      }
      // @ts-ignore
      if (node.hasAttribute('justifyContent')) {
        // @ts-ignore
        nodeProps.justifyContent = node.getAttribute('justifyContent');
      }
      // @ts-ignore
      if (node.hasAttribute('textAlign')) {
        // @ts-ignore
        nodeProps.textAlign = node.getAttribute('textAlign');
      }
      // @ts-ignore
      if (node.hasAttribute('height')) {
        // @ts-ignore
        const height = node.getAttribute('height');
        if (height.includes(',')) {
          nodeProps.height = height.split(',');
        } else {
          nodeProps.height = height;
        }
      }
      // @ts-ignore
      if (node.hasAttribute('margin')) {
        // @ts-ignore
        nodeProps.margin = node.getAttribute('margin');
      }
      // @ts-ignore
      if (node.hasAttribute('maxWidth')) {
        // @ts-ignore
        nodeProps.maxWidth = node.getAttribute('maxWidth');
      }
      // @ts-ignore
      if (node.hasAttribute('width')) {
        // @ts-ignore
        const width = node.getAttribute('width');
        if (width.includes(',')) {
          nodeProps.width = width.split(',');
        } else {
          nodeProps.width = width;
        }
      }
      // @ts-ignore
      if (node.hasAttribute('margin')) {
        // @ts-ignore
        nodeProps.margin = node.getAttribute('margin');
      }
      // @ts-ignore
      if (node.hasAttribute('gridTemplateRows')) {
        // @ts-ignore
        nodeProps.gridTemplateRows = node.getAttribute('gridTemplateRows');
      }
      // @ts-ignore
      if (node.hasAttribute('flexDirection')) {
        // @ts-ignore
        nodeProps.flexDirection = node.getAttribute('flexDirection');
      }
      // @ts-ignore
      if (node.hasAttribute('id')) {
        // @ts-ignore
        nodeProps.id = node.getAttribute('id');
      }
      break;
    case 'typography':
      // @ts-ignore
      nodeName = Typography;
      // @ts-ignore
      if (node.hasAttribute('variant')) {
        // @ts-ignore
        nodeProps.variant = node.getAttribute('variant');
      }
      // @ts-ignore
      if (node.hasAttribute('gutterBottom')) {
        // @ts-ignore
        nodeProps.gutterBottom = true;
      }
      // @ts-ignore
      if (node.hasAttribute('component')) {
        // @ts-ignore
        nodeProps.component = node.getAttribute('component');
      }
      // @ts-ignore
      if (node.hasAttribute('m')) {
        // @ts-ignore
        nodeProps.m = node.getAttribute('m');
      }
      break;
    case 'paragraph':
      // @ts-ignore
      nodeName = Typography;
      // @ts-ignore
      if (node.hasAttribute('variant')) {
        // @ts-ignore
        nodeProps.variant = node.getAttribute('variant');
      }
      // @ts-ignore
      if (node.hasAttribute('gutterBottom')) {
        // @ts-ignore
        nodeProps.gutterBottom = true;
      }
      break;
    case 'videoMessageSection':
      // @ts-ignore
      nodeName = VideoMessageSection;
      // @ts-ignore
      if (node.hasAttribute('title')) {
        // @ts-ignore
        nodeProps.title = node.getAttribute('title');
      }
      // @ts-ignore
      if (node.hasAttribute('description')) {
        // @ts-ignore
        nodeProps.description = node.getAttribute('description');
      }
      // @ts-ignore
      if (node.hasAttribute('buttonTitle')) {
        // @ts-ignore
        nodeProps.buttonTitle = node.getAttribute('buttonTitle');
      }
      // @ts-ignore
      if (node.hasAttribute('buttonLink')) {
        // @ts-ignore
        nodeProps.buttonLink = node.getAttribute('buttonLink');
      }
      break;
    case 'customisationSection':
      // @ts-ignore
      nodeName = CustomisationSection;
      // @ts-ignore
      if (node.hasAttribute('title')) {
        // @ts-ignore
        nodeProps.title = node.getAttribute('title');
      }
      // @ts-ignore
      if (node.hasAttribute('bgSrc')) {
        // @ts-ignore
        nodeProps.bgSrc = node.getAttribute('bgSrc');
      }
      // @ts-ignore
      if (node.hasAttribute('description')) {
        // @ts-ignore
        nodeProps.description = node.getAttribute('description');
      }
      // @ts-ignore
      if (node.hasAttribute('link')) {
        // @ts-ignore
        nodeProps.link = node.getAttribute('link');
      }
      // @ts-ignore
      if (node.hasAttribute('linkTitle')) {
        // @ts-ignore
        nodeProps.linkTitle = node.getAttribute('linkTitle');
      }
      // @ts-ignore
      if (node.hasAttribute('cardOneSrc')) {
        // @ts-ignore
        nodeProps.cardOneSrc = node.getAttribute('cardOneSrc');
      }
      // @ts-ignore
      if (node.hasAttribute('cardTwoSrc')) {
        // @ts-ignore
        nodeProps.cardTwoSrc = node.getAttribute('cardTwoSrc');
      }
      break;
    case 'cardGiftSection':
      // @ts-ignore
      nodeName = CardGiftSection;
      // @ts-ignore
      if (node.hasAttribute('description')) {
        // @ts-ignore
        nodeProps.description = node.getAttribute('description');
      }
      // @ts-ignore
      if (node.hasAttribute('buttonText')) {
        // @ts-ignore
        nodeProps.buttonText = node.getAttribute('buttonText');
      }
      // @ts-ignore
      if (node.hasAttribute('images')) {
        // @ts-ignore
        const images = node.getAttribute('images');
        if (images.includes(',')) {
          nodeProps.images = images.split(',');
        } else {
          nodeProps.images = images;
        }
      }
      // @ts-ignore
      if (node.hasAttribute('buttonUrl')) {
        // @ts-ignore
        nodeProps.buttonUrl = node.getAttribute('buttonUrl');
      }
      break;
    case 'banner':
      nodeName = Banner;
      // @ts-ignore
      if (node.hasAttribute('imageSrc')) {
        // @ts-ignore
        nodeProps.imageSrc = node.getAttribute('imageSrc');
      }
      // @ts-ignore
      if (node.hasAttribute('maxHeight')) {
        // @ts-ignore
        nodeProps.maxHeight = node.getAttribute('maxHeight');
      }
      break;
    case 'button':
      nodeName = Button;
      nodeProps.component = RouterLink;
      // @ts-ignore
      if (node.hasAttribute('to')) {
        // @ts-ignore
        nodeProps.to = node.getAttribute('to');
      }
      // @ts-ignore
      if (node.hasAttribute('variant')) {
        // @ts-ignore
        nodeProps.variant = node.getAttribute('variant');
      }
      // @ts-ignore
      if (node.hasAttribute('color')) {
        // @ts-ignore
        nodeProps.color = node.getAttribute('color');
      }
      // @ts-ignore
      if (node.hasAttribute('size')) {
        // @ts-ignore
        nodeProps.size = node.getAttribute('size');
      }
      // @ts-ignore
      if (node.hasAttribute('endIcon')) {
        let IconComponent = null;
        // @ts-ignore
        if (node.getAttribute('endIcon') === 'SendIcon') IconComponent = <SendIcon />;
        nodeProps.endIcon = IconComponent;
      }
      break;
  }
  // d('children', children);
  return React.createElement(nodeName, nodeProps, children);
};

const createNodes = (html: string, data?: DataProps) => {
  let docNode;
  if (window.DOMParser) {
    try {
      const parser = new DOMParser();
      docNode = parser.parseFromString(html, 'text/xml');
      if (isParseError(docNode)) {
        d('Error rendering description', html);
      }
    } catch (err) {
      console.warn('caughtpageerror', err);
    }
  } else {
    // Microsoft strikes again
    // @ts-ignore
    docNode = new ActiveXObject('Microsoft.XMLDOM');
    docNode.async = false;
    docNode.loadXML(html);
  }
  return convertNodeToReact(docNode.firstChild, 0, data);
};

const DescriptionHelper: React.FC<DescriptionHelperProps> = ({ html = '', data }) => {
  // Strip out weird meta tags...
  html = html.replace(/<meta charset="utf-8">/gi, '');
  // TODO - checkout this span situation...
  html = html.replace(/<span data-mce-fragment="1">/g, '');
  html = html.replace(/<span>/g, '');
  html = html.replace(/<\/span>/g, '');
  // Cleanup excess paragraphs
  html = html.replace(/<p>/g, '');
  html = html.replace(/<p data-mce-fragment="1">/g, '');
  html = html.replace(/<\/p>/g, '');
  html = html.replace(/<br data-mce-fragment="1">/g, ''); // No BREAKS
  html = html.replace(/<br>/g, '');
  // Remove all divs...
  html = html.replace(/<div>/g, '');
  html = html.replace(/<\/div>/g, '');

  // Hide the PageData
  html = html.replace(/\[pd([^\]]+)\]/g, '');
  html = html.replace(/\[newline([^\]]+)\]/g, '<newline$1>');
  html = html.replace(/\[divider([^\]]+)\]/g, '<divider$1>');
  // MUI Grid
  html = html.replace(/\[container\]/g, '<container>');
  html = html.replace(/\[\/container\]/g, '</container>');
  html = html.replace(/\[gridcontainer\]/g, '<gridcontainer>');
  html = html.replace(/\[\/gridcontainer\]/g, '</gridcontainer>');
  html = html.replace(/\[column1]/g, '<column1>');
  html = html.replace(/\[\/column1]/g, '</column1>');
  html = html.replace(/\[column2]/g, '<column2>');
  html = html.replace(/\[\/column2]/g, '</column2>');
  html = html.replace(/\[column3]/g, '<column3>');
  html = html.replace(/\[\/column3]/g, '</column3>');
  html = html.replace(/\[column4]/g, '<column4>');
  html = html.replace(/\[\/column4]/g, '</column4>');
  html = html.replace(/\[column5]/g, '<column5>');
  html = html.replace(/\[\/column5]/g, '</column5>');
  html = html.replace(/\[column6]/g, '<column6>');
  html = html.replace(/\[\/column6]/g, '</column6>');
  html = html.replace(/\[column7]/g, '<column7>');
  html = html.replace(/\[\/column7]/g, '</column7>');
  html = html.replace(/\[column8]/g, '<column8>');
  html = html.replace(/\[\/column8]/g, '</column8>');
  html = html.replace(/\[column9]/g, '<column9>');
  html = html.replace(/\[\/column9]/g, '</column9>');
  html = html.replace(/\[column10]/g, '<column10>');
  html = html.replace(/\[\/column10]/g, '</column10>');
  html = html.replace(/\[column11]/g, '<column11>');
  html = html.replace(/\[\/column11]/g, '</column11>');
  html = html.replace(/\[column12]/g, '<column12>');
  html = html.replace(/\[\/column12]/g, '</column12>');
  html = html.replace(/\[box([^\]]+)\]/g, '<box$1>');
  html = html.replace(/\[\/box]/g, '</box>');
  // Banner Helper
  html = html.replace(/\[banner([^\]]+)\]/g, '<banner$1>');
  html = html.replace(/\[\/banner]/g, '</banner>');
  // Button Helper
  html = html.replace(/\[button([^\]]+)\]/g, '<button$1>');
  html = html.replace(/\[\/button]/g, '</button>');
  // FAQ Helper
  html = html.replace(/\[faq([^\]]+)\]/g, '<faq$1>');
  html = html.replace(/\[\/faq]/g, '</faq>');
  // VideoMessageSection Helper
  html = html.replace(/\[videoMessageSection([^\]]+)\]/g, '<videoMessageSection$1>');
  // customisationSection Helper
  html = html.replace(/\[customisationSection([^\]]+)\]/g, '<customisationSection$1>');
  // cardGiftSection Helper
  html = html.replace(/\[cardGiftSection([^\]]+)\]/g, '<cardGiftSection$1>');
  // Image Helper
  html = html.replace(/\[image([^\]]+)\]/g, '<image$1>');
  html = html.replace(/\[\/image]/g, '</image>');
  // ProductCarouselList Helper
  html = html.replace(/\[productCarouselList([^\]]+)\]/g, '<productCarouselList$1>');
  html = html.replace(/\[\/productCarouselList]/g, '</productCarouselList>');
  // Form
  html = html.replace(/\[form([^\]]+)\]/g, '<form$1>');
  html = html.replace(/\[\/form]/g, '</form>');
  // FAQ Helper
  html = html.replace(/\[paragraph([^\]]+)\]/g, '<paragraph$1>');
  html = html.replace(/\[\/paragraph]/g, '</paragraph>');
  // FAQ Helper
  html = html.replace(/\[typography([^\]]+)\]/g, '<typography$1>');
  html = html.replace(/\[\/typography]/g, '</typography>');
  // Brand Helper
  html = html.replace(/\[brands([^\]]+)\]/g, '<brands$1>');
  // DynamicCarousel
  html = html.replace(/\[dynamicCarousel([^\]]+)\]/g, '<dynamicCarousel$1>');
  html = html.replace(/\[\/dynamicCarousel]/g, '</dynamicCarousel>');
  // DynamicCarousel Slide
  html = html.replace(/\[slide ([^\]]+)\]/g, '<dynamicCarouselSlide $1>');
  html = html.replace(/\[\/slide]/g, '</dynamicCarouselSlide>');
  // Slider Helper
  html = html.replace(/\[slider([^\]]+)\]/g, '<slider$1>');
  // Grid Helper
  html = html.replace(/\[grid([^\]]+)\]/g, '<grid$1>');
  // Configure Button List
  html = html.replace(/\[configureButtonList([^\]]+)\]/g, '<configureButtonList$1>');

  const node = createNodes('<div>' + html + '</div>', data);
  return <>{node}</>;
};

export default DescriptionHelper;
